import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, RotateCcw, Truck, Mail, MapPin, Phone, Building } from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';

const COMPANY_INFO = {
  name: 'Bella Casa Interiors',
  legalName: 'Bella Casa Interiors',
  email: 'support@bellacasainteriors.in',
  phone: '+91 7977700933',
  address: 'Plot.no 21, Navketan Society, Ideal Colony, Kothrud, Pune, Maharashtra 411038',
  website: 'https://bellacasainteriors.in',
};

const PageWrapper = ({ children, title, icon: Icon }) => (
  <div className="min-h-screen bg-stone-50">
    {/* Header */}
    <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
      <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
        <Link to="/login" className="p-2 hover:bg-stone-100 rounded-lg transition-colors">
          <ArrowLeft className="w-5 h-5 text-stone-600" />
        </Link>
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-6 h-6 text-amber-600" />}
          <h1 className="text-xl font-semibold text-stone-900">{title}</h1>
        </div>
      </div>
    </header>
    
    {/* Content */}
    <main className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6 md:p-8">
        {children}
      </div>
    </main>
    
    {/* Footer */}
    <footer className="border-t border-stone-200 bg-white mt-12">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex flex-wrap gap-4 justify-center text-sm text-stone-500">
          <Link to="/legal/privacy" className="hover:text-amber-600">Privacy Policy</Link>
          <span>•</span>
          <Link to="/legal/terms" className="hover:text-amber-600">Terms & Conditions</Link>
          <span>•</span>
          <Link to="/legal/refund" className="hover:text-amber-600">Refund Policy</Link>
          <span>•</span>
          <Link to="/legal/shipping" className="hover:text-amber-600">Shipping Policy</Link>
          <span>•</span>
          <Link to="/legal/contact" className="hover:text-amber-600">Contact Us</Link>
        </div>
        <p className="text-center text-xs text-stone-400 mt-4">
          © {new Date().getFullYear()} {COMPANY_INFO.name}. All rights reserved.
        </p>
      </div>
    </footer>
  </div>
);

const SectionTitle = ({ children }) => (
  <h2 className="text-lg font-semibold text-stone-800 mt-8 mb-4 first:mt-0">{children}</h2>
);

const Paragraph = ({ children }) => (
  <p className="text-stone-600 mb-4 leading-relaxed">{children}</p>
);

const List = ({ items }) => (
  <ul className="list-disc list-inside text-stone-600 mb-4 space-y-2">
    {items.map((item, idx) => (
      <li key={idx}>{item}</li>
    ))}
  </ul>
);

// Privacy Policy Page
export function PrivacyPolicy() {
  return (
    <PageWrapper title="Privacy Policy" icon={Shield}>
      <p className="text-sm text-stone-500 mb-6">Last updated: {formatDate(new Date(), { longMonth: true })}</p>
      
      <SectionTitle>1. Introduction</SectionTitle>
      <Paragraph>
        {COMPANY_INFO.name} (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) is committed to protecting your privacy. 
        This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
        when you use our project management software and services.
      </Paragraph>

      <SectionTitle>2. Information We Collect</SectionTitle>
      <Paragraph>We collect information that you provide directly to us, including:</Paragraph>
      <List items={[
        'Account information: name, email address, phone number, company name',
        'Project data: project details, tasks, budgets, client information',
        'Payment information: billing address, payment method details (processed securely via Razorpay)',
        'Communications: messages, support requests, feedback',
        'Usage data: how you interact with our services'
      ]} />

      <SectionTitle>3. How We Use Your Information</SectionTitle>
      <Paragraph>We use the collected information to:</Paragraph>
      <List items={[
        'Provide, maintain, and improve our services',
        'Process transactions and send related information',
        'Send technical notices, updates, and support messages',
        'Respond to your comments and questions',
        'Analyze usage patterns to improve user experience',
        'Detect, prevent, and address technical issues and fraud'
      ]} />

      <SectionTitle>4. Data Sharing</SectionTitle>
      <Paragraph>
        We do not sell your personal information. We may share information with:
      </Paragraph>
      <List items={[
        'Service providers who assist in our operations (payment processors, cloud hosting)',
        'Legal authorities when required by law or to protect our rights',
        'Business partners with your consent'
      ]} />

      <SectionTitle>5. Data Security</SectionTitle>
      <Paragraph>
        We implement appropriate technical and organizational measures to protect your data, 
        including encryption, secure servers, and access controls. However, no method of 
        transmission over the Internet is 100% secure.
      </Paragraph>

      <SectionTitle>6. Your Rights</SectionTitle>
      <Paragraph>You have the right to:</Paragraph>
      <List items={[
        'Access and receive a copy of your personal data',
        'Rectify inaccurate personal data',
        'Request deletion of your personal data',
        'Withdraw consent at any time',
        'Lodge a complaint with a supervisory authority'
      ]} />

      <SectionTitle>7. Contact Us</SectionTitle>
      <Paragraph>
        For privacy-related inquiries, please contact us at{' '}
        <a href={`mailto:${COMPANY_INFO.email}`} className="text-amber-600 hover:underline">
          {COMPANY_INFO.email}
        </a>
      </Paragraph>
    </PageWrapper>
  );
}

// Terms and Conditions Page
export function TermsConditions() {
  return (
    <PageWrapper title="Terms & Conditions" icon={FileText}>
      <p className="text-sm text-stone-500 mb-6">Last updated: {formatDate(new Date(), { longMonth: true })}</p>
      
      <SectionTitle>1. Acceptance of Terms</SectionTitle>
      <Paragraph>
        By accessing or using {COMPANY_INFO.name}&apos;s services, you agree to be bound by these 
        Terms and Conditions. If you do not agree to these terms, please do not use our services.
      </Paragraph>

      <SectionTitle>2. Description of Service</SectionTitle>
      <Paragraph>
        {COMPANY_INFO.name} provides a cloud-based project management software solution for 
        interior design and construction professionals. Our services include project tracking, 
        budget estimation, vendor management, invoicing, and AI-powered budget assistance.
      </Paragraph>

      <SectionTitle>3. User Accounts</SectionTitle>
      <Paragraph>To use our services, you must:</Paragraph>
      <List items={[
        'Create an account with accurate and complete information',
        'Maintain the security of your account credentials',
        'Notify us immediately of any unauthorized access',
        'Be responsible for all activities under your account'
      ]} />

      <SectionTitle>4. Subscription and Payments</SectionTitle>
      <Paragraph>
        Certain features of our service require payment. By subscribing to paid features:
      </Paragraph>
      <List items={[
        'You agree to pay all applicable fees',
        'Payments are processed securely through Razorpay',
        'Subscription fees are billed in advance on a monthly/annual basis',
        'AI Budget Estimation credits are charged per use after free tier exhaustion'
      ]} />

      <SectionTitle>5. AI Budget Estimation Service</SectionTitle>
      <Paragraph>
        Our AI-powered budget estimation feature provides estimates based on the information you provide. 
        Please note:
      </Paragraph>
      <List items={[
        'AI estimates are for reference purposes and may not reflect actual costs',
        'You are responsible for verifying all estimates before use',
        'We do not guarantee the accuracy of AI-generated budgets',
        'Usage is tracked and charged according to our pricing policy'
      ]} />

      <SectionTitle>6. Intellectual Property</SectionTitle>
      <Paragraph>
        All content, features, and functionality of our service are owned by {COMPANY_INFO.name} 
        and are protected by intellectual property laws. You may not reproduce, distribute, or 
        create derivative works without our permission.
      </Paragraph>

      <SectionTitle>7. Limitation of Liability</SectionTitle>
      <Paragraph>
        To the maximum extent permitted by law, {COMPANY_INFO.name} shall not be liable for any 
        indirect, incidental, special, consequential, or punitive damages arising from your use 
        of our services.
      </Paragraph>

      <SectionTitle>8. Termination</SectionTitle>
      <Paragraph>
        We may terminate or suspend your account at any time for violations of these terms. 
        Upon termination, your right to use our services will immediately cease.
      </Paragraph>

      <SectionTitle>9. Governing Law</SectionTitle>
      <Paragraph>
        These terms shall be governed by and construed in accordance with the laws of India. 
        Any disputes shall be subject to the exclusive jurisdiction of the courts in Mumbai, Maharashtra.
      </Paragraph>

      <SectionTitle>10. Contact</SectionTitle>
      <Paragraph>
        For questions about these terms, contact us at{' '}
        <a href={`mailto:${COMPANY_INFO.email}`} className="text-amber-600 hover:underline">
          {COMPANY_INFO.email}
        </a>
      </Paragraph>
    </PageWrapper>
  );
}

// Refund Policy Page
export function RefundPolicy() {
  return (
    <PageWrapper title="Refund Policy" icon={RotateCcw}>
      <p className="text-sm text-stone-500 mb-6">Last updated: {formatDate(new Date(), { longMonth: true })}</p>
      
      <SectionTitle>1. Overview</SectionTitle>
      <Paragraph>
        At {COMPANY_INFO.name}, we strive to provide high-quality services. This refund policy 
        outlines the terms under which refunds may be granted for our paid services.
      </Paragraph>

      <SectionTitle>2. Subscription Plans</SectionTitle>
      <Paragraph>For monthly and annual subscription plans:</Paragraph>
      <List items={[
        'Full refund within 7 days of initial purchase if unsatisfied with the service',
        'No refunds for partial months after the 7-day period',
        'Annual subscriptions: Pro-rated refund available within first 30 days',
        'Cancellation will be effective at the end of the current billing period'
      ]} />

      <SectionTitle>3. AI Budget Estimation Credits</SectionTitle>
      <Paragraph>For AI-powered budget estimation services:</Paragraph>
      <List items={[
        'Free tier credits are non-refundable as they are complimentary',
        'Paid credits: Refund available if the AI service fails to generate a response',
        'No refunds for successfully generated estimates, regardless of accuracy',
        'Credit balance can be used until account termination'
      ]} />

      <SectionTitle>4. How to Request a Refund</SectionTitle>
      <Paragraph>To request a refund:</Paragraph>
      <List items={[
        `Email us at ${COMPANY_INFO.email} with your account details`,
        'Include your reason for the refund request',
        'Provide the transaction ID or invoice number',
        'Allow 5-7 business days for review and processing'
      ]} />

      <SectionTitle>5. Refund Processing</SectionTitle>
      <Paragraph>
        Approved refunds will be processed through the original payment method (Razorpay). 
        Please allow 5-10 business days for the refund to reflect in your account, depending 
        on your bank or payment provider.
      </Paragraph>

      <SectionTitle>6. Non-Refundable Items</SectionTitle>
      <Paragraph>The following are not eligible for refunds:</Paragraph>
      <List items={[
        'Services used beyond the refund eligibility period',
        'Accounts terminated due to terms of service violations',
        'Custom development or consulting services',
        'Third-party integrations or services purchased through our platform'
      ]} />

      <SectionTitle>7. Contact Us</SectionTitle>
      <Paragraph>
        For refund inquiries, please contact our support team at{' '}
        <a href={`mailto:${COMPANY_INFO.email}`} className="text-amber-600 hover:underline">
          {COMPANY_INFO.email}
        </a>
      </Paragraph>
    </PageWrapper>
  );
}

// Shipping Policy Page (for digital/SaaS products)
export function ShippingPolicy() {
  return (
    <PageWrapper title="Shipping & Delivery Policy" icon={Truck}>
      <p className="text-sm text-stone-500 mb-6">Last updated: {formatDate(new Date(), { longMonth: true })}</p>
      
      <SectionTitle>1. Digital Service Delivery</SectionTitle>
      <Paragraph>
        {COMPANY_INFO.name} is a cloud-based software service (SaaS). As such, there is no 
        physical shipping involved. All services are delivered electronically through our 
        web platform.
      </Paragraph>

      <SectionTitle>2. Service Activation</SectionTitle>
      <Paragraph>Upon successful registration and payment:</Paragraph>
      <List items={[
        'Account activation: Immediate upon registration',
        'Subscription features: Activated immediately after payment confirmation',
        'AI Credits: Available instantly upon purchase',
        'Premium features: Accessible within minutes of payment processing'
      ]} />

      <SectionTitle>3. Access Requirements</SectionTitle>
      <Paragraph>To access our services, you need:</Paragraph>
      <List items={[
        'A compatible web browser (Chrome, Firefox, Safari, Edge - latest versions)',
        'Stable internet connection',
        'Valid email address for account verification',
        'No special software installation required'
      ]} />

      <SectionTitle>4. Service Availability</SectionTitle>
      <Paragraph>
        Our service is available 24/7, with occasional scheduled maintenance windows. 
        We aim for 99.9% uptime. Any planned maintenance will be communicated in advance 
        via email notification.
      </Paragraph>

      <SectionTitle>5. Data Export</SectionTitle>
      <Paragraph>
        You can export your project data at any time through our platform. Supported 
        export formats include PDF for reports and quotations, and Excel for data exports. 
        Data export is instant and free of charge.
      </Paragraph>

      <SectionTitle>6. Geographic Availability</SectionTitle>
      <Paragraph>
        Our services are currently available throughout India. International access may be 
        subject to local regulations. Some payment methods may be restricted based on your 
        location.
      </Paragraph>

      <SectionTitle>7. Contact</SectionTitle>
      <Paragraph>
        For delivery or access issues, contact us at{' '}
        <a href={`mailto:${COMPANY_INFO.email}`} className="text-amber-600 hover:underline">
          {COMPANY_INFO.email}
        </a>
      </Paragraph>
    </PageWrapper>
  );
}

// Contact Us Page
export function ContactUs() {
  return (
    <PageWrapper title="Contact Us" icon={Mail}>
      <div className="grid md:grid-cols-2 gap-8">
        {/* Contact Information */}
        <div>
          <SectionTitle>Get in Touch</SectionTitle>
          <Paragraph>
            We&apos;d love to hear from you. Whether you have questions about our services, 
            need technical support, or want to provide feedback, our team is here to help.
          </Paragraph>
          
          <div className="space-y-4 mt-6">
            <div className="flex items-start gap-3">
              <Building className="w-5 h-5 text-amber-600 mt-1" />
              <div>
                <p className="font-medium text-stone-800">{COMPANY_INFO.legalName}</p>
                <p className="text-stone-600 text-sm">{COMPANY_INFO.name}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-amber-600" />
              <a href={`mailto:${COMPANY_INFO.email}`} className="text-amber-600 hover:underline">
                {COMPANY_INFO.email}
              </a>
            </div>
            
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-amber-600" />
              <a href={`tel:${COMPANY_INFO.phone}`} className="text-stone-600 hover:text-amber-600">
                {COMPANY_INFO.phone}
              </a>
            </div>
            
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-amber-600 mt-1" />
              <p className="text-stone-600">{COMPANY_INFO.address}</p>
            </div>
          </div>
        </div>
        
        {/* Support Hours */}
        <div>
          <SectionTitle>Support Hours</SectionTitle>
          <div className="bg-stone-50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-stone-600">Monday - Friday</span>
              <span className="font-medium text-stone-800">9:00 AM - 6:00 PM IST</span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-600">Saturday</span>
              <span className="font-medium text-stone-800">10:00 AM - 4:00 PM IST</span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-600">Sunday</span>
              <span className="font-medium text-stone-500">Closed</span>
            </div>
          </div>
          
          <div className="mt-6">
            <SectionTitle>Response Time</SectionTitle>
            <List items={[
              'Email queries: Within 24-48 business hours',
              'Technical support: Within 4-8 business hours',
              'Payment issues: Within 24 hours (priority)'
            ]} />
          </div>
        </div>
      </div>

      <div className="mt-8 p-4 bg-amber-50 rounded-lg border border-amber-200">
        <p className="text-amber-800 text-sm">
          <strong>For urgent matters:</strong> If you&apos;re experiencing a critical issue with 
          payments or account access, please include &ldquo;URGENT&rdquo; in your email subject line for 
          priority handling.
        </p>
      </div>
    </PageWrapper>
  );
}

// Main router component
export default function LegalPages() {
  const { page } = useParams();
  
  switch (page) {
    case 'privacy':
      return <PrivacyPolicy />;
    case 'terms':
      return <TermsConditions />;
    case 'refund':
      return <RefundPolicy />;
    case 'shipping':
      return <ShippingPolicy />;
    case 'contact':
      return <ContactUs />;
    default:
      return <PrivacyPolicy />;
  }
}
